// The API surface every screen talks to. Today it is backed by the in-memory
// mock (./mockApi). To go live, implement this same interface against the real
// cloud API and change the one `api` assignment below — nothing else moves.
import type {
  Adm, AdminAccountSummary, AdminAuditEvent, AdminDeviceSummary, AdminSiteSummary, Authority, BackstopStatus, Battery, BatteryPack, Behaviour, CommandsStatus, CommissioningStep, ComplianceRecord, Consent, ConsentLogEntry, DerStatusStatus, Device, DeviceKind,
  CommandView, DeviceCommandRequest, DeviceCommandSubmission, EconomicSettings, EconomicSettingsPatch,
  DiagnosticRegister, DocContent, DocMeta, EnergyPeriod, EnergySummary, RecordedEnergyDay, FirmwareCampaignAbort, FirmwareCampaignView, FirmwareInfo, FleetAssociation, FleetSite, GridJurisdiction, HandoverResult, HwsDeclaration,
  DemandCharge, InstallerApplication, InstallerOrg, InstallerOrganisationView, Inverter, InventoryKind, InventoryUnit, InventoryValidation, CorrectionResult, DeclarationResult, Mode, AppNotification, NotificationPrefs, OrgAuditEvent, OrgMembership, OrgRole, PairingStatus, PowerFlow, Site, SupportOverview, Tariff, TariffPeriod, TariffTier, TariffType, TelemetryStatus, TouSlot, UiPrefs, User, Warranty, Weather,
  UpdateJobView, UpdatePlan,
  VppDispatchRequest, VppEvent, VppPartnerView, VppSiteStatus, VppTokenResponse, VppWebhookDelivery,
} from './types'
import type { BleClient } from './bleClient'

/** Result of `login`: either signed in with a user, or a 2FA challenge to complete
 *  via `verifyTwoFactor` (APP-ACC-07). Discriminated on `challengeRequired`. */
export type LoginResult = { user: User } | {
  challengeRequired: true
  email: string
  challengeToken: string
  enrollmentRequired: boolean
  enrollmentSecret?: string
}

export interface ApiClient {
  // auth
  /** Sign in. Returns the user, OR a 2FA challenge when the account has opt-in 2FA
   *  enabled — the caller then collects a 6-digit code and calls `verifyTwoFactor`. */
  login(email: string, password: string): Promise<LoginResult>
  /** Complete a 2FA challenge from `login` (APP-ACC-07): a 6-digit code → signed in. */
  verifyTwoFactor(email: string, code: string, challengeToken: string): Promise<User>
  /** Return the caller-specific authenticator secret/URI before enabling MFA. */
  getTwoFactorSetup(): Promise<{ secret: string; uri: string }>
  /** Enable/disable opt-in 2FA on the caller's profile (APP-ACC-07); persisted. */
  setTwoFactor(enabled: boolean, code?: string): Promise<User>
  /** Self-serve account creation (APP-ACC-01). A homeowner can create an account
   *  **any time** — it is NOT gated by the installer. In the demo this provisions
   *  the per-email world (same as `login`) and signs in; `name` is the display
   *  name. The installer later transfers a system to this email, which the owner
   *  approves on Roles & ownership (Ownership screen). */
  signup(name: string, email: string, password: string): Promise<User>
  /** End the session (APP-ACC-06): clears the token/identity, so signing out
   *  actually ends the session rather than leaving a privilege-bearing token in
   *  place. (The mock also suppresses its lazy default identity — see mockApi.) */
  signOut(): Promise<void>
  getCurrentUser(): Promise<User | null>
  // installer identity (APP-ACC-09 / CLD-PRT-09) — roles are server-owned
  /** Request installer access: credentials go to the back-office queue and the
   *  account turns `installerStatus: pending`. Only the admin decision elevates
   *  the role (SYS-SW-07) — this call never does. Re-apply after a rejection. */
  applyInstaller(org: InstallerOrg): Promise<User>
  /** The caller's own application (status + decision audit), or null. */
  getInstallerApplication(): Promise<InstallerApplication | null>
  /** Back-office queue, pending first (admin account only — 403 otherwise). */
  adminListInstallerApplications(): Promise<InstallerApplication[]>
  /** Approve/reject an application (admin only). Approval takes effect
   *  immediately: the applicant's next request sees role 'installer'. */
  adminDecideInstallerApplication(userId: string, approve: boolean): Promise<InstallerApplication>
  /** Unified Risen operations directory and controlled account actions (CLD-PRT-10). */
  adminListAccounts(query?: string): Promise<AdminAccountSummary[]>
  adminListSites(query?: string): Promise<AdminSiteSummary[]>
  adminListDevices(query?: string): Promise<AdminDeviceSummary[]>
  adminListAudit(): Promise<AdminAuditEvent[]>
  adminSetInstallerCredential(accountId: string, status: 'approved' | 'suspended' | 'revoked', reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }>
  adminSetAccountLock(accountId: string, locked: boolean, reason: string, ticket: string): Promise<{ account: AdminAccountSummary; audit: AdminAuditEvent }>
  // site & live
  getSite(): Promise<Site>
  /** Residences this account may enter, with the session's active site marked. */
  listResidences(): Promise<import('./types').ResidenceList>
  /** Switch the session's active Residence (re-issues the bearer token). */
  switchResidence(siteId: string): Promise<import('./types').ResidenceSummary>
  /** Reset this demo world to its seed defaults (demo analog of account/data
   *  deletion, CLD-DAT-04 / APP-NFR-04). Deletes the caller's stored state; the
   *  next reads re-seed. Returns the count of items cleared. */
  resetSiteData(): Promise<{ reset: boolean; deletedItems: number }>
  getPowerFlow(): Promise<PowerFlow>
  /** Subscribe to the live power-flow ticker. Returns an unsubscribe function. */
  subscribeLive(cb: (pf: PowerFlow) => void): () => void
  /** Device-link telemetry (Phase 9): whether a fresh MQTT frame from the mock
   *  comms module arrived, its `source` (device vs sim), age, the latest frame and
   *  the recent-frames ring. The CLD-ING-06 frame contract; freshness = APP-HOM-25;
   *  connectivity in each frame = ICD-COM-05. The offline mock returns a sim frame
   *  with `online:false` (no device link exists offline). */
  getTelemetry(): Promise<TelemetryStatus>
  // battery & control
  getBattery(): Promise<Battery>
  /** Every physical 7 kWh pack, each with its own identity and live telemetry. */
  getBatteryPacks(): Promise<BatteryPack[]>
  setBackupReserve(pct: number): Promise<Battery>
  setSocLimits(ceilingPct: number, floorPct: number): Promise<Battery>
  getAuthority(): Promise<Authority>
  /** The downlink command log + the currently-active setpoint command (Phase 10 —
   *  the CSIP-Aus slice, CLD-CMD-01 / ICD-API-07). The cloud aggregator translates
   *  the DNSP's DERControl into a generic versioned command (GRI-CSP-03) and sends
   *  it down the device's outbound MQTT channel — the device never speaks CSIP-Aus
   *  (invariant #3). `active` is gated on the command's TTL; the log is the record. */
  getCommands(): Promise<CommandsStatus>
  /** The per-DNSP jurisdiction config bound to this site (GRI-CSP-05), read-only —
   *  utility-server URL, poll cadence, per-site export default (the GRI-DEV-02 revert
   *  target) and backstop adapter, plus the full config set. Admin-managed posture. */
  getGridJurisdiction(): Promise<GridJurisdiction>
  /** The DERStatus reports the cloud relayed back to the DNSP (GRI-CSP-02 return) —
   *  the conformance-evidence log of the actuation the device reported. */
  getDerStatus(): Promise<DerStatusStatus>
  /** DEMO: simulate a DNSP-issued EMERGENCY backstop (GRI-BST-01/02) on this site.
   *  `start` opens a window (naming the issuing authority) and drives the live
   *  authority banner + notification + ADM record (linked to the non-revocable DNSP
   *  consent c-1, GRI-BST-03); `stop` clears it. This simulates the DNSP signal — it
   *  is not a homeowner control. Returns the resulting window (null when stopped). */
  triggerBackstop(
    action: 'start' | 'stop',
    opts?: { jurisdiction?: string; limitW?: number; durationMinutes?: number },
  ): Promise<BackstopStatus>
  getModes(): Promise<Mode[]>
  /** Toggle a mode on/off. The effective (`active`) mode is computed server-side
   *  from the fixed precedence — never set directly by the client. */
  setModeEnabled(id: string, enabled: boolean): Promise<Mode[]>
  /** Standalone behaviour toggles (APP-HOM-20), independent of the mode stack. */
  getBehaviour(): Promise<Behaviour>
  /** Toggle one behaviour flag; returns the full Behaviour so the UI reconciles
   *  both switches. Both feed the simulator (zero-export / peak-demand shaving). */
  setBehaviour(flag: keyof Behaviour, enabled: boolean): Promise<Behaviour>
  getSchedule(): Promise<TouSlot[]>
  /** Replace the TOU forced charge/discharge schedule (full-list replace).
   *  Drives the simulated dispatch windows. Returns the updated schedule. */
  setSchedule(slots: TouSlot[]): Promise<TouSlot[]>
  getTariff(): Promise<Tariff>
  setTariffType(type: TariffType): Promise<Tariff>
  /** Replace the TOU rate periods (add / edit / delete are all expressed as the
   *  new full list). Returns the updated tariff. */
  setTariffPeriods(periods: TariffPeriod[]): Promise<Tariff>
  /** Set the single flat usage rate ($/kWh). Returns the updated tariff. */
  setFlatRate(rate: number): Promise<Tariff>
  /** Replace the tiered-plan consumption tiers (full-list replace). */
  setTariffTiers(tiers: TariffTier[]): Promise<Tariff>
  /** Set the demand-charge config (window + rate + days). Returns the tariff. */
  setDemandCharge(demand: DemandCharge): Promise<Tariff>
  getWeather(): Promise<Weather>
  // energy
  getEnergy(period: EnergyPeriod): Promise<EnergySummary>
  /** Genuine five-minute device telemetry for one local calendar day. */
  getRecordedEnergyDay(date: string): Promise<RecordedEnergyDay>
  // info & consent
  getNotifications(): Promise<AppNotification[]>
  /** Mark notifications read (specific ids, or all when omitted). Persists;
   *  returns the updated feed. The always-on safety alarm is never removed. */
  markNotificationsRead(ids?: string[]): Promise<AppNotification[]>
  /** Notification channel + category prefs (APP-HOM-08). */
  getNotificationPrefs(): Promise<NotificationPrefs>
  /** Replace the notification prefs (full object). The safety category is forced
   *  on server-side, so the returned prefs may differ from what was sent. */
  setNotificationPrefs(prefs: NotificationPrefs): Promise<NotificationPrefs>
  getConsents(): Promise<Consent[]>
  /** Grant or revoke a consent (APP-HOM-07) — the homeowner's opt-out control.
   *  Revoking the VPP consent drops the site from every partner cohort and refuses
   *  any dispatch to it (CLD-VPP-03), enforced at the data layer (CLD-DAT-03). A
   *  NON-revocable consent (the DNSP connection condition) is refused with a 403 —
   *  a mandatory network condition is not a preference. Returns the updated list. */
  setConsent(id: string, status: 'active' | 'inactive'): Promise<Consent[]>
  /** Append-only consent activity log (APP-HOM-07). */
  getConsentLog(): Promise<ConsentLogEntry[]>

  // ---- Phase-12: VPP dispatch (ICD-API-02 / CLD-VPP-* / GRI-VPP-*) ----
  /** The homeowner's own view of VPP dispatch on their site: enrolment (the consent
   *  that governs it — revoke to opt out), the dispatch in force, and the recent
   *  per-site event records with their settlement baseline + delivered energy
   *  (GRI-VPP-02). Consent-scoped (CLD-VPP-03 / CLD-DAT-03). */
  getVppStatus(): Promise<VppSiteStatus>
  /** OAuth2 client-credentials grant for the MOCK VPP partner (ICD-API-02). Returns a
   *  PARTNER bearer carrying the partner's registered scopes; the client stores it and
   *  sends it on the `/partner/*` calls below. The demo binds the grant to the caller's
   *  own site as the one REAL member of the otherwise-simulated fleet, so the whole
   *  round-trip is visible in their app. Mock: a static secret, no real IdP. */
  vppPartnerToken(clientId: string, clientSecret: string): Promise<VppTokenResponse>
  /** The partner sandbox (CLD-VPP-06): the CONSENT-GATED simulated fleet + the event and
   *  webhook history. A site without an active VPP consent is absent from `fleet` (not
   *  redacted — absent) and shows only in `withheldSites` (CLD-DAT-03). `fleet:read`. */
  vppPartnerFleet(): Promise<VppPartnerView>
  /** Raise a dispatch event against a cohort (CLD-VPP-01/04). Every site is SCREENED
   *  against its in-force DNSP limit / backstop, its reserve floor and any
   *  higher-precedence mode BEFORE a setpoint is issued (GRI-VPP-01) — clamped or
   *  refused, never allowed to breach. Partial failure is normal: the event carries a
   *  per-site result with an outcome + reason code. `dispatch:write`. */
  vppDispatch(req: VppDispatchRequest): Promise<VppEvent>
  /** The partner's dispatch history with the settlement evidence (GRI-VPP-02). `settlement:read`. */
  vppPartnerEvents(): Promise<VppEvent[]>
  /** The webhook callbacks delivered to the partner (CLD-VPP-01). DEMO: recorded, not
   *  POSTed — there is no partner server to call. `settlement:read`. */
  vppPartnerWebhooks(): Promise<VppWebhookDelivery[]>
  /** Battery warranty registration (STR-PRD-08), retained with the asset across
   * account unpair/pair events. */
  getWarranty(): Promise<Warranty>
  /** Home-page block visibility prefs (APP-HOM-23), account-synced. */
  getUiPrefs(): Promise<UiPrefs>
  /** Replace the home-page block visibility prefs (merged over defaults server-side). */
  setUiPrefs(prefs: UiPrefs): Promise<UiPrefs>
  /** Automated-decision transparency records — the retained what/when/why + data
   *  used for each significant automated decision (APP-HOM-22 / CLD-ADM-01). */
  getAdm(): Promise<Adm>
  /** Open a human-review case against a past automated decision. Returns the
   *  records with that decision's review case attached (idempotent). */
  requestAdmReview(decisionId: string): Promise<Adm>
  getInverter(): Promise<Inverter>
  getDevices(): Promise<Device[]>
  /** Cloud projection of device-authoritative primary pairing. */
  getPairing(deviceId?: string): Promise<PairingStatus>
  /** Native builds implement these through BLE. The web demonstrator posts the
   * same revisioned device event contract to its simulator adapter. */
  // `physical_button_unpair` is a device-originated event, never an app-writable
  // action: it arrives on the device-authenticated leg (the dongle's ledger sync).
  recordLocalPairing(action: 'pair' | 'unpair', expectedRevision: number, deviceId: string, residenceId?: string): Promise<PairingStatus>
  /** Current homeowner remote release; completion comes only from device state. */
  remoteUnpairDevice(expectedRevision: number, deviceId?: string): Promise<PairingStatus>
  /** Cancel a pending owner remote unpair (APP-OWN-13A). No-op after applied. */
  cancelRemoteUnpair(deviceId?: string): Promise<PairingStatus>
  /** Support/Admin queue a dongle-confirmed detach against an open case with a
   * recorded reason and an online dongle; staff cannot pair the next account. */
  forceDetachPairing(deviceId: string, expectedRevision: number, caseId: string, reason: string): Promise<PairingStatus>
  /** Public help/FAQ without sign-in (APP-ACC-06). */
  getHelpFaq(): Promise<import('./types').HelpFaqItem[]>
  /** Residence-linked support enquiries (APP-HOM-12). */
  listSupportEnquiries(): Promise<import('./types').SupportEnquiry[]>
  createSupportEnquiry(subject: string, attachDiagnostics: boolean): Promise<import('./types').SupportEnquiry>
  postSupportMessage(enquiryId: string, body: string): Promise<import('./types').SupportEnquiry>
  requestSupportCallback(enquiryId: string, phone?: string): Promise<import('./types').SupportEnquiry>
  manageHouseholdMember(action: 'invite' | 'remove', email: string, access?: 'Family' | 'Viewer', deviceId?: string): Promise<PairingStatus>
  // installer
  /** Commissioning wizard steps. With a `site` id, the residence being
   *  commissioned (installer `full` access required); without, the caller's own site. */
  getCommissioning(site?: string): Promise<CommissioningStep[]>
  /** Advance the optional commissioning workflow. This never activates or gates the
   *  inverter and does not manufacture a compliance/test pass. */
  advanceCommissioning(site?: string): Promise<CommissioningStep[]>
  /** The HWS relay declaration for a residence (APP-PAIR-16), or null if undeclared. */
  getHwsDeclaration(site?: string): Promise<HwsDeclaration | null>
  /** Declare the HWS relay + "hot by" comfort deadline in the wizard (APP-PAIR-16);
   *  persisted with the wizard state and reflected on the compliance record. */
  setHwsDeclaration(relayConnected: boolean, hotByHour: number | undefined, site?: string): Promise<HwsDeclaration>
  /** Live registers. With a `site` id, current installer pairing/full access is
   *  required; without one, the caller's own Residence is used. */
  getDiagnostics(site?: string): Promise<DiagnosticRegister[]>
  getCompliance(): Promise<ComplianceRecord>
  getFirmware(): Promise<FirmwareInfo[]>
  /** Apply the signed A/B update to a component; the version change persists and
   *  a "firmware installed" notification is appended (APP-INS-05). */
  applyFirmwareUpdate(component: string): Promise<FirmwareInfo[]>

  // ---- firmware update lifecycle + region profile (phase 4.5) ----
  /** Every outstanding update for the Residence with its classification, state,
   *  automatic-update deadline and device-reported versions, plus the SEPARATE
   *  region-profile picture (APP-HOM-31, APP-INS-05A/05B/05C, CLD-OTA-03/07*).
   *  Read by both the homeowner update screen and the installer firmware screen —
   *  one answer, so the two cannot disagree about the commissioning gate.
   *
   *  `site` ADDRESSES A RESIDENCE, and an installer surface must pass it. The
   *  commissioning gate is a fact about the home being commissioned, not about the
   *  practitioner's own account: without it the wizard and the Firmware screen read
   *  the installer's OWN site, so a blocking update on the Residence in front of
   *  them is invisible and one on their own site blocks a sign-off it has nothing
   *  to do with. Omitted, it is the caller's own Residence (the homeowner case);
   *  supplied, the caller must hold a membership of it or the read is refused. */
  getUpdates(site?: string): Promise<UpdatePlan>
  /** APP-HOM-31A — start a queued update now. "Now" overrides the WAIT, never the
   *  gate: an offline device or an outstanding commissioning-blocking update is
   *  refused with the reason, not started. */
  startUpdate(jobId: string): Promise<UpdateJobView>
  /** Retry an update that failed or rolled back. */
  retryUpdate(jobId: string): Promise<UpdateJobView>
  /** Fleet-wide rollout status (CLD-OTA-01/02/04). Installer/staff only — a
   *  homeowner has no business seeing a cohort. */
  getFirmwareCampaigns(): Promise<FirmwareCampaignView[]>
  // ---- Stopping a rollout (CLD-OTA-02) — a RISEN-STAFF act ----
  //
  // Irreversible: a halt can be resumed, an abort cannot. `reason` IS NOT OPTIONAL
  // and the server refuses without it, because an abort with no recorded reason is
  // not auditable; it is truncated to 300 characters where the server truncates it.
  //
  // THE RESULT NAMES BOTH HALVES OF THE OUTCOME — the jobs actually stopped, and
  // the ones the abort could not reach because the device is already writing its
  // standby partition. The second list is not an error; it is the honest limit of
  // what a cloud abort can do, and the device's own A/B rollback owns those units.
  // A caller that showed only `aborted` would claim a cohort stopped when part of
  // it demonstrably did not.
  //
  // TWO METHODS, NOT A `scope` PARAMETER — the same rule as the staff serial
  // re-assignment below. The acting scope is the ROUTE, so the server's record of
  // who stopped the rollout cannot be a claim the client made about itself.
  //
  // THE INSTALLER CANNOT DO THIS AT ALL. `abortFirmwareCampaign` used to exist and
  // was surfaced on the installer Firmware screen, bounded to a cohort lying
  // entirely inside the caller's own fleet. Stopping a rollout is not the
  // installer's role, so the method, the route and that cohort-scope rule are all
  // gone rather than merely hidden.

  /** Stop a rollout campaign on an Admin session (`POST /admin/firmware/campaigns/abort`). */
  adminAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort>
  /** The same stop, made on a Support session; the route is what records the scope. */
  supportAbortFirmwareCampaign(campaignId: string, reason: string): Promise<FirmwareCampaignAbort>
  getFleet(): Promise<FleetSite[]>
  /** Pair an accessory. With a `site` id, pair on a residence the installer is
   *  commissioning (`full` access required); without, the caller's own site. */
  pairDevice(kind: DeviceKind, site?: string): Promise<Device[]>
  // Residence creation and installer primary-pairing release.
  /** Create a Residence with mandatory address and NMI and add it to the current
   *  installer jobs. Device/dongle binding is completed during local pairing. */
  createResidence(label: string, address: string, nmi: string): Promise<FleetSite[]>
  /** Queue the preferred installer-to-homeowner handoff (APP-OWN-05/05A/05B).
   *  Self-contained: it queues the installer's OWN device-confirmed unpair, so the
   *  result is `pending` until the dongle confirms. On confirmation the installer
   *  releases primary pairing and retains a basic-status fleet row; the homeowner
   *  then pairs locally. Nobody accepts anything — `ownerEmail` is optional contact
   *  information that grants no access (APP-ACC-03). */
  handoverResidence(site: string, ownerEmail: string): Promise<HandoverResult>
  /** Remove the installer's pairing/membership so the Residence drops off their
   *  own commissioning jobs. The ORGANISATION keeps a retained basic-status fleet
   *  row (APP-OWN-06B) until it — or the homeowner (APP-OWN-18A) — removes it.
   *  Residence identity and audit/test history remain for later matching
   *  and authorized onsite ledger reads. */
  unpairResidence(site: string): Promise<FleetSite[]>

  // ---- Installer Organisation, practitioners and inventory (APP-ACC-08–08L) ----
  // Every call below is scoped to ONE organisation. `organisationId` is optional
  // on the wire and defaults to the acting organisation the client has stored
  // (setActingOrganisation); a practitioner who belongs to more than one
  // organisation and has named none is REFUSED (400) rather than guessed at —
  // that refusal is APP-ACC-08L, and the client must not paper over it.

  /** The acting organisation, the caller's own membership, the manager-only
   *  practitioner roster and every organisation the caller may act for
   *  (APP-ACC-08A/08L). A practitioner sees an empty roster by design. */
  getInstallerOrganisation(organisationId?: string): Promise<InstallerOrganisationView>
  /** INVITE a practitioner to the acting organisation (manager only, APP-ACC-08A).
   *  The membership is written `pending` and authorizes nothing until the named
   *  account accepts it — a manager may offer a link, never impose one. */
  linkPractitioner(userId: string, role: OrgRole, organisationId?: string): Promise<OrgMembership>
  /** Accept an invitation addressed to the AUTHENTICATED account. There is no
   *  parameter naming another subject, by construction. */
  acceptOrgInvitation(organisationId: string): Promise<OrgMembership>
  /** End a practitioner's organisation relationship (manager only, APP-ACC-08I).
   *  The person's access ends; the organisation's inventory is untouched. */
  revokePractitioner(userId: string, reason: string, organisationId?: string): Promise<{ revoked: boolean; membership: OrgMembership | null }>
  /** The organisation audit log, newest first (CLD-PRT-01K). */
  getOrganisationAudit(organisationId?: string): Promise<OrgAuditEvent[]>

  /** The organisation's shipment inventory (APP-ACC-08E/08F). Without an
   *  `organisationId` the caller sees the union over their ACTIVE memberships —
   *  never anything derived from the person alone (APP-ACC-08I). */
  getInventory(organisationId?: string): Promise<InventoryUnit[]>
  /** Submit a shipment declaration by manual serial entry (APP-ACC-08C). The
   *  result names EVERY refused serial and, for one held elsewhere, the holding
   *  organisation and the Support/Admin re-assignment path (APP-ACC-08D). */
  declareInventory(serials: string[], kind?: InventoryKind, organisationId?: string): Promise<DeclarationResult>
  /** Withdraw a declaration (manager only, APP-ACC-08J); its row leaves the active
   *  inventory (APP-ACC-08K) and the serial becomes enterable again. */
  withdrawInventory(serial: string, reason: string, organisationId?: string): Promise<InventoryUnit>
  /** Correct a declaration (manager only, APP-ACC-08J): withdraw one serial and
   *  re-declare the replacements through the SAME validation. */
  correctInventory(fromSerial: string, toSerials: string[], kind?: InventoryKind, organisationId?: string): Promise<CorrectionResult>
  /** The installation-time gate used by the pairing flow: is this serial in the
   *  acting organisation's inventory? Unknown and cross-organisation serials both
   *  fail, and the refusal names the holder. Changes nothing. */
  validateInventorySerial(serial: string, organisationId?: string): Promise<InventoryValidation>

  // ---- Staff serial re-assignment (SYS-FUN-17J1 / CLD-PRT-01E1) ----
  // THE ONE PATH THAT MOVES A HELD SERIAL WITHOUT THE HOLDER'S COOPERATION, and
  // the reason `declareInventory` can name a way out when it refuses a serial as
  // `assigned-elsewhere` (APP-ACC-08D): without it a defunct organisation strands
  // a unit forever.
  //
  // ONE BEHAVIOUR, TWO GUARDS, hence two methods rather than a `scope` parameter.
  // Support has it because a stranded serial arrives as a support case; Admin has
  // it because it is an inventory correction. The acting scope is recorded on the
  // audit event, so which of the two acted is answerable afterwards — a parameter
  // would have made that record a caller-supplied claim. It is NOT a pairing
  // mutation, so CLD-PRT-02E's prohibition on Support pairing is untouched.
  //
  // `reason` is mandatory (≥ 3 characters, server-enforced) and is written to
  // BOTH organisations' audit logs — the one that lost the unit and the one that
  // gained it — because each has to be able to explain the change from its own
  // records. 404 when no inventory row exists for the serial.

  /** Move one held serial to a named organisation with a recorded reason (admin). */
  adminReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit>
  /** The same move, made on a Support session; the audit records the acting scope. */
  supportReassignInventorySerial(serial: string, organisationId: string, reason: string): Promise<InventoryUnit>

  /** Remove the organisation's retained basic-status fleet row for one Residence
   *  (APP-OWN-06B). Returns the caller's fleet as it stands afterwards. */
  removeFleetSite(site: string, organisationId?: string): Promise<FleetSite[]>

  // ---- Homeowner view of retained associations (APP-OWN-18/18A) ----
  /** Every Installer Organisation that still holds a fleet association for the
   *  homeowner's Residence, with the basic-status scope it provides (APP-OWN-18). */
  getFleetAssociations(): Promise<FleetAssociation[]>
  /** Remove the Residence from one organisation's fleet (APP-OWN-18A). Changes no
   *  pairing, no Residence record, no inventory and no other organisation's row. */
  removeFleetAssociation(organisationId: string): Promise<FleetAssociation[]>

  // ---- Acting-organisation selection (APP-ACC-08L) — CLIENT-side state ----
  // The choice is the client's, so it is stored by the client (like the session
  // token) and SENT on every organisation-scoped request; there is no server
  // resource for "which organisation is this phone acting for", and inventing one
  // would make a UI preference look like an authorization grant. These two are the
  // only ApiClient members with no REST operation besides signup/signOut.
  /** The organisation this client is currently acting for, or null if unchosen. */
  getActingOrganisation(): Promise<string | null>
  /** Choose (or clear) the acting organisation. Persisted on this client. */
  setActingOrganisation(organisationId: string | null): Promise<void>

  // ---- Phase-13b: in-app documents (APP-HOM-19 / CLD-PRT-08) ----
  /** Versioned documents/manuals available to the site's product model — each with
   *  its latest version + version history (latest is the in-app default). */
  listDocuments(model?: string): Promise<DocMeta[]>
  /** One document's content at a version (default: latest), rendered in-app. */
  getDocument(id: string, version?: string): Promise<DocContent>

  // ---- Phase-13b: support role (APP-ACC-02 / CLD-PRT-02) ----
  /** The support agent's consent-gated, read-only health view of the demo site.
   *  Support (the 4th role) sees device OK/error + SoH-vs-warranted only, and only
   *  while the site's support-access consent is active. 403 for non-support. */
  supportOverview(): Promise<SupportOverview>

  // ---- Stage 4.4: the device-command lifecycle (Stage 3.3 taxonomy) ----
  /**
   * Submit ONE control change against ONE named Connected Device.
   *
   * `deviceId` is required and is not optional-by-convenience: a Residence
   * identifier is never a broadcast (SYS-FUN-19S), and the screen has to have
   * shown the homeowner which device the action targets before it gets here
   * (device-bound APP-HOM-29E, or Residence delivery under APP-HOM-32/32C). Every submission is issued on the ECONOMIC tier
   * and attributed to the signed-in account — a routine export limit is a
   * constraint on the grid tier and is refused here rather than accepted as a
   * homeowner's command (invariant #5).
   *
   * `commandId` is the idempotency key (generated when omitted): a retried call
   * returns the FIRST record rather than issuing a second command. A newer
   * command for the same device + control supersedes an earlier undelivered one,
   * and the superseded records come back so the UI can retire their pending state
   * honestly instead of leaving them spinning.
   *
   * The returned command is almost always `pending` — the device has not answered
   * yet. Poll `getDeviceCommands` for the result; do not treat submission as
   * application.
   */
  submitDeviceCommand(request: DeviceCommandRequest): Promise<DeviceCommandSubmission>
  /**
   * The command records for one Connected Device (or the whole Residence when
   * `deviceId` is omitted), newest first, each carrying the full apply-result
   * taxonomy: requested / delivered / unverified / applied / clamped / rejected /
   * superseded / expired, with the classified reason and the tier that out-ranked
   * a clamp or refusal. `delivered` is what separates a command still queued in
   * the cloud from one handed to an authenticated dongle (APP-SEC-03).
   */
  getDeviceCommands(deviceId?: string): Promise<CommandView[]>

  // ---- Stage 4.4: the account's economic-settings profile (APP-HOM-32) ----
  /**
   * The signed-in account's economic-settings envelope for the selected
   * Residence: the profile (account, Residence scope, explicit target Connected
   * Devices, revision + history), the settings, and the Residence time zone every
   * schedule window is interpreted in. Barriered on both axes server-side, so a
   * former owner's settings are unreachable here (APP-HOM-32A / CLD-CMD-04B).
   * `profile.isDefault` is the honest "this account has chosen nothing yet"
   * signal that makes the default-self-consumption display truthful rather than
   * an assumption (APP-HOM-32B).
   */
  getEconomicSettings(): Promise<EconomicSettings>
  /**
   * Save a revision THROUGH the profile rather than at bare site settings — the
   * accepted change is stamped with the account, the Residence, the scope and the
   * explicit target devices (CLD-CMD-04D). A patch names only what changed.
   */
  updateEconomicSettings(patch: EconomicSettingsPatch): Promise<EconomicSettings>

  // ---- Stage 4.3: the onsite BLE surface (APP-INS-08) ----
  /**
   * The dongle's LOCAL BLE surface, spoken through the v1 message contract
   * frozen in `contracts/ble-v1.ts` (APP-INS-08: one message schema and workflow
   * for every compatible dongle).
   *
   * WHY IT SITS ON `ApiClient` AT ALL, given BLE is not the cloud. Because the
   * swap point has to stay ONE swap point: a screen asks the adapter for its
   * link the same way it asks for its cloud, and each adapter answers with the
   * transport it actually has — the mock adapter with an in-process scripted
   * dongle (`mockDongle.ts`), the HTTP adapter with the dev bridge to the
   * composed Simulated Dongle, and the native builds of phase 5.3 with a real
   * BLE radio. No screen branches on which.
   *
   * The client is created once per adapter and holds the session, so a caller
   * that authenticated earlier in the flow does not re-authenticate.
   */
  ble(): BleClient

}

import { mockApi } from './mockApi'
import { HttpApiClient } from './httpApi'

// 🔌 swap point. With VITE_API_BASE set (deploy), talk to the real demo cloud
// (cloud/); unset (offline dev), keep the in-memory mock. No screen changes.
// VITE_STREAM_URL (Phase 4) is the SSE power-flow origin — the client prefers it
// for live data and falls back to polling /power-flow when it's absent or failing.
const apiBase = import.meta.env.VITE_API_BASE
export const api: ApiClient = apiBase
  ? new HttpApiClient(apiBase, import.meta.env.VITE_STREAM_URL)
  : mockApi

export * from './types'
export type { BleClient, BleOutcome, BleProver, BleTransport } from './bleClient'
